import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  LinearProgress
} from "@mui/material";
import { TagBox } from "devextreme-react";
import { ValueChangedEvent } from "devextreme/ui/tag_box";
import {
  ScopedRole__SolutionScope,
  ScopedRole__TenantScope
} from "model/schema/globalTypes";
import { useState } from "react";
import { useStyles } from "./RolesStyle";

type RoleType = ScopedRole__SolutionScope | ScopedRole__TenantScope;

interface RoleProps<T extends RoleType> {
  roles?: T[];
  allRoles: T[];
  onRoleUpdated: (roles: T[]) => void;
  loadingRoles: boolean;
  label?: string;
  height?: number;
  userName?: string;
  myUserName?: string | null;
  lastAdminWarning?: string;
  adminRole?: T;
  adminCount?: number;
}

function Roles<T extends RoleType>({
  roles,
  allRoles = [],
  onRoleUpdated,
  loadingRoles,
  label,
  height,
  userName,
  myUserName,
  lastAdminWarning,
  adminRole,
  adminCount
}: RoleProps<T>) {
  const classes = useStyles();

  const [isFocused, setIsFocused] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState("");

  const handleChange = async (options: ValueChangedEvent) => {
    const { value } = options;
    const removedValue =
      options.previousValue?.find((item: string) => !value.includes(item)) ||
      null;

    if (options.event) {
      if (removedValue === adminRole && adminCount === 1 && lastAdminWarning) {
        setDialogMessage(lastAdminWarning);
        setOpenDialog(true);
        return;
      }

      if (
        removedValue === adminRole &&
        userName &&
        myUserName &&
        userName === myUserName
      ) {
        setDialogMessage(
          "You are not allowed to drop your own admin role. Ask another admin to do this for you."
        );
        setOpenDialog(true);
        return;
      }
      onRoleUpdated(value);
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };
  if (!roles) return null;

  return (
    <div className={classes.cell} data-testid="select-group">
      {loadingRoles ? <LinearProgress data-testid="loading-roles" /> : null}
      <TagBox
        dataSource={allRoles}
        value={roles}
        multiline={true}
        onValueChanged={handleChange}
        placeholder={undefined}
        showDropDownButton
        focusStateEnabled={true}
        onFocusIn={() => setIsFocused(true)}
        onFocusOut={() => setIsFocused(false)}
        width={"100%"}
        label={label}
        labelMode="floating"
        style={{
          height: height ?? "100%",
          border: isFocused
            ? "solid 2px #3AA9AE"
            : "solid 1px rgba(0, 0, 0, 0.23)"
        }}
        noDataText="no roles"
        className={classes.root}
      />
      <Dialog open={openDialog} onClose={handleCloseDialog}>
        <DialogContent>
          <DialogContentText>{dialogMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseDialog}
            color="primary"
            variant="contained"
            className={classes.button}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}

export default Roles;
